// Simple route bundle prefetching to reduce page-switch lag
// Uses dynamic imports that webpack will cache after first load.
// Plain import() on purpose: a webpackPrefetch hint here made webpack prefetch
// every one of these pages at boot, for logged-out visitors too (see App.tsx).

import {
  dashboardAPI,
  simulationsAPI,
  billingAPI,
  seatsAPI,
  historyAPI,
  companyAPI,
  adminAPI,
} from '../services/api';

type RoutePath =
  | '/dashboard'
  | '/pathways'
  | '/practice'
  | '/progress'
  | '/settings'
  | '/practice-conversation'
  | '/practice-review';

export function prefetchPage(path: string) {
  const p = path as RoutePath;
  switch (p) {
    case '/dashboard':
      import('../pages/Dashboard');
      break;
    case '/pathways':
      import('../pages/Pathways');
      break;
    case '/practice':
      import('../pages/Practice');
      break;
    case '/progress':
      import('../pages/Progress');
      break;
    case '/settings':
      import('../pages/Settings');
      break;
    case '/practice-conversation':
      import('../pages/PracticeConversationEnhanced');
      break;
    case '/practice-review':
      import('../pages/PracticeReview');
      break;
    default:
      break;
  }
}

export function prefetchCommon() {
  const idle = (cb: () => void) =>
    (window as any).requestIdleCallback
      ? (window as any).requestIdleCallback(cb)
      : setTimeout(cb, 200);
  idle(() => {
    prefetchPage('/dashboard');
    prefetchPage('/pathways');
    prefetchPage('/practice');
    prefetchPage('/progress');
  });
}

/* ── Data prefetch ──────────────────────────────────────────────────
 * Warms the in-memory GET cache (services/api.ts cachedGet) for the
 * heavy endpoints behind every sidebar destination + the Settings
 * tabs, so navigating there hits warm cache instead of a cold fetch.
 *
 * Design constraints:
 *   - Runs ONCE per session, after auth resolves, at browser idle +
 *     a grace delay — it must never compete with the current page's
 *     own requests for bandwidth/server time.
 *   - Two wave structure: the universally-hit endpoints first, the
 *     role-gated Settings bundle a beat later.
 *   - Every warm is fire-and-forget with a swallowed error: a failed
 *     prefetch costs nothing (the page fetches normally on visit).
 *   - Role-gated endpoints are only warmed for roles that can call
 *     them — warming /seats as an employee would just produce a 403
 *     in the console and noise in the server log.
 *
 * TTL reality check: entries warmed here live for their endpoint's
 * TTL (12s–5min). The long-TTL responses (simulations catalogue,
 * plans, invite link) get the real "lightning" win; short-TTL ones
 * (dashboard, seats) only feel instant when the user navigates soon
 * after load — which is exactly the common case this targets.
 */
export function prefetchData(user: any) {
  if (!user) return;
  const role = String(user.role || '');
  const isOwner = role === 'owner';
  const canManageRoles = isOwner || role === 'admin';

  const idle = (cb: () => void) =>
    (window as any).requestIdleCallback
      ? (window as any).requestIdleCallback(cb)
      : setTimeout(cb, 400);
  const warm = (fn: () => Promise<any>) => {
    fn().catch(() => {
      /* prefetch is best-effort */
    });
  };

  idle(() => {
    setTimeout(() => {
      // Wave 1 — sidebar destinations every user visits.
      warm(() => dashboardAPI.getData()); // Home (10s TTL)
      warm(() => simulationsAPI.getSimulations({})); // Practice (5min TTL — the heavy one)
      warm(() => billingAPI.getPlans()); // Subscription (5min TTL)
      warm(() => seatsAPI.getMine()); // practice gating surface (12s TTL)
      warm(() => historyAPI.getRecordingsSummary()); // Progress (12s TTL)

      // Wave 2 — Settings bundle, staggered so wave 1 owns the wire.
      setTimeout(() => {
        warm(() => companyAPI.getMyInviteLink()); // Account tab (5min TTL)
        if (canManageRoles) {
          warm(() => adminAPI.getTeamMembers()); // Team access + Seats bench (30s TTL)
        }
        if (isOwner) {
          warm(() => seatsAPI.getBoard()); // Seats board (12s TTL)
          warm(() => billingAPI.getTransactions({ limit: 20 })); // Transactions (30s TTL)
        }
      }, 1200);
    }, 800);
  });
}
